// RequireAuth — wraps protected routes; redirects anonymous users to /login
// with `?next=` plumbing so the deep-link is recoverable (per W2 §3.2 #31 + #33).

import { Navigate, useLocation } from "react-router-dom";
import { type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth/AuthProvider";

interface RequireAuthProps {
  children: ReactNode;
}

export function RequireAuth({ children }: RequireAuthProps) {
  const auth = useAuth();
  const location = useLocation();

  if (auth.status === "loading") {
    return (
      <div
        data-testid="require-auth-loading"
        className="min-h-[100dvh] flex items-center justify-center bg-paper text-ink-2"
      >
        <Loader2 className="w-6 h-6 animate-spin" aria-hidden />
        <span className="sr-only">載入中</span>
      </div>
    );
  }

  if (auth.status === "anonymous" || !auth.user) {
    const next = encodeURIComponent(location.pathname + location.search);
    // Carry *why* when we know it. Signing in elsewhere revokes this session
    // by design (w2.md §4.7), and a bare redirect drops the user onto a login
    // form for a password that worked a minute ago with no explanation. The
    // reason also tells them that signing back in here will sign the other
    // device out, which is the part that otherwise turns into a loop.
    const reason = auth.revoked ? "&reason=session-expired" : "";
    return <Navigate to={`/login?next=${next}${reason}`} replace />;
  }

  return <>{children}</>;
}
